/**
 * @file "Send a booking link" — the QR / share-link dialog for appointment booking.
 * @module pages/healthcare/appointments/BookingLinkModal
 *
 * S8 of ClickUp 86d2w0fwz, and the direct counterpart of `formsInTray/FormShareLinkModal` (F11 of
 * 86d3t4ev9). Same shape, same exposure hygiene, one important difference — see below.
 *
 * ═══ WHY A LINK AT ALL ══════════════════════════════════════════════════════════════════════
 * `/patient/bookings` (the anonymous booking form) identifies the patient by a fuzzy name/email
 * match, so nothing there can be enforced per patient. This link carries a per-link capability
 * (`appointment_booking_links.token`), so the patient is known before the request is read — which
 * is what makes single-use consumption and the per-patient cap enforceable at all.
 *
 * ═══ 🚨 EVERY *GENERATE* MINTS A NEW LINK — UNLIKE THE FORMS MODAL ══════════════════════════
 * `issueFormLink` is idempotent: reopening that dialog returns the same link. `POST
 * booking-links` is deliberately NOT, because a booking link is per-send — a second link must not
 * silently invalidate the first, and each is independently revocable and independently traceable
 * to the appointment it produced. So a practitioner who generates twice has handed out TWO live
 * links. That is intended; what is not acceptable is it being invisible, which is why the link's
 * own id is on screen beside the expiry and why Revoke acts on THAT id.
 *
 * ═══ NOTHING IS MINTED UNTIL "GENERATE" IS CLICKED ══════════════════════════════════════════
 * This dialog used to issue on open. It cannot, now that the scope is chosen here: issuing first
 * would mint a link before the practitioner had said what it was for, and changing the scope
 * afterwards would either orphan a live link or kill a URL they had already sent. So opening is
 * inert — it loads the type list and waits.
 *
 * Changing the type after generating therefore takes the link OFF SCREEN but does NOT revoke it.
 * Auto-revoking would silently break a URL that may already be in the patient's inbox; the note
 * that replaces the link says so, and names the id, rather than letting it vanish quietly.
 *
 * ═══ THE TOKEN IS NEVER SHOWN ON ITS OWN ════════════════════════════════════════════════════
 * It appears in exactly two places, both of which are the whole URL: the QR payload and the
 * copyable link. Nothing copies the bare value — a token on a clipboard or in a screenshot is a
 * live capability to book as a named patient, detached from any context that says so.
 */

import React, { useCallback, useEffect, useState } from "react";
import { Alert, Button, Divider, Modal, Select, Space, Spin, Tooltip, Typography } from "antd";
import {
	ExclamationCircleOutlined, CopyOutlined, MailOutlined, MessageOutlined, StopOutlined,
} from "@ant-design/icons";
import { QRCodeSVG } from "qrcode.react";
import { format } from "date-fns";

import { ENV } from "../../../config/runtimeEnv";
import { issueBookingLink, revokeBookingLink } from "../../../api/healthcare/Appointment/BookingLinkApi";
import { fetchBookableAppointmentTypesPatient } from "../../../api/healthcare/Appointment/AppointmentApi";
import { buildPatientBookingLink } from "../../../utils/healthcare/bookingLink";
import { buildBookingLinkCommunication, BookingLinkChannel } from "../../../utils/healthcare/bookingLinkNotification";
import { sendCommunication } from "../../../api/communications/communicationApi";
import { useNotifications } from "../../../context/NotificationContext";
import { AppointmentType } from "../../../types/appointments/AppointmentTypes";
import { Patient } from "../../../types/healthcare/PatientTypes";
import type { UserUuid } from "../../../types/identity";

const { Paragraph, Text } = Typography;

/** ≥256px, plus a 4-module quiet zone — what makes it scan from across a desk. */
const QR_SIZE_PX = 256;

/**
 * The "no scope" option. A sentinel rather than `null`, because antd's `Select` reads a `null`
 * value as "nothing chosen" and paints the placeholder — the global case would look unanswered
 * when it is in fact the default answer. It becomes `appointment_type_id: null` on the wire.
 */
const ANY_TYPE = "__any__";

type ScopeValue = typeof ANY_TYPE | number;

export interface BookingLinkModalProps {
	isOpen: boolean;
	onClose: () => void;
	/** The patient the link is minted for. `id` is the whole request — without it nothing issues. */
	patient: Partial<Patient> | null;
	/**
	 * The scope the type selector STARTS on. The practitioner may change it before generating —
	 * this is a default, not a lock. Omitted (or null) starts on "Any appointment type".
	 */
	appointmentTypeId?: number | null;
	/** Pins the booking to one practitioner. Optional — an unscoped link lets the patient choose. */
	practitionerId?: UserUuid | null;
}

const BookingLinkModal: React.FC<BookingLinkModalProps> = ({
	isOpen,
	onClose,
	patient,
	appointmentTypeId,
	practitionerId,
}) => {
	const [link, setLink] = useState<string | null>(null);
	const [linkId, setLinkId] = useState<number | null>(null);
	const [expiresAt, setExpiresAt] = useState<string | null>(null);
	const [token, setToken] = useState<string | null>(null);
	const [isIssuing, setIsIssuing] = useState(false);
	const [issueFailed, setIssueFailed] = useState(false);
	const [isRevoked, setIsRevoked] = useState(false);
	const [sendingChannel, setSendingChannel] = useState<BookingLinkChannel | null>(null);
	const [scope, setScope] = useState<ScopeValue>(appointmentTypeId ?? ANY_TYPE);
	const [types, setTypes] = useState<AppointmentType[]>([]);
	const [isLoadingTypes, setIsLoadingTypes] = useState(false);
	/** The id of a link taken off screen by a scope change — still live, and said so on screen. */
	const [supersededLinkId, setSupersededLinkId] = useState<number | null>(null);
	const { showSuccess, showError } = useNotifications();

	const scopedTypeId = scope === ANY_TYPE ? null : scope;

	const notificationsEnabled = ENV.VITE_NOTIFICATIONS_ENABLED === "true";
	const patientName = patient
		? `${patient.first_name ?? ""} ${patient.last_name ?? ""}`.trim()
		: "";

	const issue = useCallback(async () => {
		setIsIssuing(true);
		setIssueFailed(false);
		try {
			const issued = await issueBookingLink({
				patientId: patient?.id,
				appointmentTypeId: scopedTypeId,
				practitionerId,
			});
			// `buildPatientBookingLink` throws on an empty token rather than emitting `t=undefined`,
			// and it does so INSIDE this try — a link that cannot be built is an issue failure, not
			// a broken URL rendered as if it worked.
			setLink(buildPatientBookingLink(issued.token));
			setToken(issued.token);
			setLinkId(issued.id);
			setExpiresAt(issued.expires_at);
			// The note about the link this one replaced has done its job — the id line below now
			// carries which link is on screen.
			setSupersededLinkId(null);
		} catch {
			// Nothing partial is left on screen: a stale QR beside a failed issue is a link the
			// practitioner would hand over believing it had just been prepared.
			setLink(null);
			setToken(null);
			setLinkId(null);
			setExpiresAt(null);
			setIssueFailed(true);
		} finally {
			setIsIssuing(false);
		}
	}, [patient?.id, scopedTypeId, practitionerId]);

	/**
	 * Opening is INERT — it resets, and waits for Generate. ⚠️ `issue()` must never be called from
	 * here: this effect also re-runs whenever the dialog is reopened, and issuing on open is what
	 * minted links before the practitioner had chosen what they were for.
	 */
	useEffect(() => {
		if (!isOpen) return;
		setLink(null);
		setToken(null);
		setLinkId(null);
		setExpiresAt(null);
		setIssueFailed(false);
		setIsRevoked(false);
		setSupersededLinkId(null);
		setScope(appointmentTypeId ?? ANY_TYPE);
	}, [isOpen, appointmentTypeId]);

	/**
	 * The types this patient may book — the global bookable set MERGED with anything assigned to
	 * them individually (`AppointmentTypeController::bookable()`), which is exactly what the
	 * booking form will honour. The practitioner-side `fetchAppointmentTypes()` returns every type
	 * including ones no patient can book, and `getPatientAppointmentTypes()` returns only the
	 * individual assignments — either alone would offer a scope that produces a dead link.
	 */
	useEffect(() => {
		if (!isOpen || !patient?.id) return;
		let cancelled = false;

		setIsLoadingTypes(true);
		fetchBookableAppointmentTypesPatient(patient.id)
			.then((list) => { if (!cancelled) setTypes(list ?? []); })
			.catch(() => {
				// A global link is still perfectly issuable without the list, so this degrades to
				// "Any appointment type" rather than blocking the dialog.
				if (!cancelled) { setTypes([]); showError("Failed to load appointment types"); }
			})
			.finally(() => { if (!cancelled) setIsLoadingTypes(false); });

		return () => { cancelled = true; };
	}, [isOpen, patient?.id]);

	/**
	 * A link already on screen was minted for the OLD scope, so it goes back to the pre-generate
	 * state — but it is not revoked. Every issue mints its own row, and silently killing a URL the
	 * practitioner may already have handed over is not a side effect a dropdown should have.
	 */
	const handleScopeChange = (value: ScopeValue) => {
		setScope(value);
		if (link !== null) setSupersededLinkId(linkId);
		if (link !== null || isRevoked || issueFailed) {
			setLink(null);
			setToken(null);
			setLinkId(null);
			setExpiresAt(null);
			setIssueFailed(false);
			setIsRevoked(false);
		}
	};

	/**
	 * ═══ EXPOSURE HYGIENE ════════════════════════════════════════════════════════════════════
	 * A QR on a screen is readable by anyone in the room, and this one books an appointment as a
	 * named patient. In a shared consulting room a dialog left sitting open while the practitioner
	 * is elsewhere is real exposure — so it closes as soon as this window stops being the thing in
	 * front of them.
	 *
	 * `blur` covers alt-tabbing, `visibilitychange` covers the tab being backgrounded or the screen
	 * locked, `popstate` covers navigating away inside the SPA. ⚠️ Closing is NOT lossless here:
	 * reopening starts from an empty dialog and generating mints a NEW link rather than returning
	 * this one (see the file header). The already issued link keeps working until it expires or is
	 * revoked.
	 */
	useEffect(() => {
		if (!isOpen) return;

		const closeIfHidden = () => { if (document.visibilityState === "hidden") onClose(); };
		window.addEventListener("blur", onClose);
		window.addEventListener("popstate", onClose);
		document.addEventListener("visibilitychange", closeIfHidden);
		return () => {
			window.removeEventListener("blur", onClose);
			window.removeEventListener("popstate", onClose);
			document.removeEventListener("visibilitychange", closeIfHidden);
		};
	}, [isOpen, onClose]);

	const handleCopy = async () => {
		if (!link) return;
		try {
			await navigator.clipboard.writeText(link);
			showSuccess("Link copied");
		} catch {
			showError("Could not copy the link. Select it and copy manually.");
		}
	};

	const handleSend = async (channel: BookingLinkChannel) => {
		if (!link || !token) return;
		setSendingChannel(channel);
		try {
			// The token that is actually on screen, not a re-issue: the patient must receive the
			// same link the QR encodes, or the emailed copy and the scanned one are two different
			// capabilities — and only one of them can ever be used.
			await sendCommunication(
				buildBookingLinkCommunication({
					token,
					patient,
					expiresAt,
					recipientUserId: patient?.id,
					channel,
				}),
			);
			showSuccess(channel === "email" ? "Link emailed to the patient" : "Link sent by SMS");
		} catch {
			showError(channel === "email" ? "Failed to email the link" : "Failed to send the SMS");
		} finally {
			setSendingChannel(null);
		}
	};

	/**
	 * Confirmed, because it is destructive in a way nothing on screen shows: the link in the
	 * patient's inbox stops working the moment this returns, and there is no undo.
	 */
	const handleRevoke = () => {
		if (linkId === null) return;
		const id = linkId;

		Modal.confirm({
			title: "Revoke this booking link?",
			icon: <ExclamationCircleOutlined />,
			content:
				"This QR code and any copy already sent to the patient will stop working immediately. "
				+ "Use this if the code was shown to the wrong person or sent to the wrong number.",
			okText: "Revoke link",
			okType: "danger",
			cancelText: "Cancel",
			onOk: async () => {
				try {
					await revokeBookingLink(id);
					// The QR and the URL go with it — leaving them on screen would invite handing
					// over a link that has just been turned off.
					setLink(null);
					setToken(null);
					setIsRevoked(true);
					showSuccess("Booking link revoked — it no longer works");
				} catch {
					showError("Could not revoke the link. It still works.");
				}
			},
		});
	};

	const expiryLine = expiresAt
		? (() => {
			try {
				return `This link works until ${format(new Date(expiresAt), "d MMM yyyy, h:mm a")}.`;
			} catch {
				return "This link expires.";
			}
		})()
		: "This link does not expire.";

	const busy = isIssuing || sendingChannel !== null;

	return (
		<Modal
			open={isOpen}
			onCancel={onClose}
			width={480}
			destroyOnClose
			title="Send a booking link"
			footer={<Button onClick={onClose}>Close</Button>}
		>
			<div data-testid="booking-link-modal" style={{ display: "flex", flexDirection: "column", gap: 12 }}>
				<Paragraph style={{ marginBottom: 0 }}>
					{patientName
						? `${patientName} can scan this with their phone camera to book an appointment.`
						: "The patient can scan this with their phone camera to book an appointment."}{" "}
					The link books one appointment and then stops working.
				</Paragraph>

				{/*
					The scope, chosen BEFORE anything is minted, and still changeable afterwards —
					see the file header for why it cannot be the other way round.
				*/}
				<div>
					<Text strong style={{ fontSize: 12, display: "block", marginBottom: 4 }}>
						Appointment type
					</Text>
					<Select<ScopeValue>
						data-testid="booking-link-type-select"
						value={scope}
						onChange={handleScopeChange}
						loading={isLoadingTypes}
						disabled={isIssuing}
						style={{ width: "100%" }}
						options={[
							{ value: ANY_TYPE, label: "Any appointment type" },
							...types.map((type) => ({ value: type.id, label: type.name })),
						]}
					/>
					<Text type="secondary" data-testid="booking-link-scope-hint" style={{ fontSize: 12 }}>
						{scope === ANY_TYPE
							? "Patient chooses on the booking form."
							: "The booking form is locked to this type."}
					</Text>
				</div>

				{isIssuing ? (
					<div style={{ textAlign: "center", padding: 48 }}>
						<Spin />
					</div>
				) : link ? (
					<>
						{/*
							White plate behind the code regardless of theme, plus a 4-module quiet zone.
							A QR rendered on a dark surface, or flush to its container edge, is the single
							most common reason a phone camera refuses to lock on.
						*/}
						<div
							style={{
								alignSelf: "center",
								background: "#ffffff",
								padding: 16,
								borderRadius: 8,
								lineHeight: 0,
							}}
						>
							<QRCodeSVG
								data-testid="booking-link-qr"
								value={link}
								size={QR_SIZE_PX}
								level="M"
								marginSize={4}
								bgColor="#ffffff"
								fgColor="#000000"
								title="Appointment booking link"
							/>
						</div>

						{/*
							The full URL, selectable. This is the ONLY rendering of the token anywhere in
							this component, and it is inseparable from the link it belongs to.
						*/}
						<Text
							data-testid="booking-link-url"
							code
							style={{ userSelect: "text", wordBreak: "break-all", fontSize: 11 }}
						>
							{link}
						</Text>

						<Space wrap>
							<Button icon={<CopyOutlined />} data-testid="booking-link-copy" onClick={handleCopy}>
								Copy link
							</Button>
							<Tooltip title={
								!notificationsEnabled
									? "Notifications are switched off for this environment."
									: !patient?.email ? "This patient has no email address on file." : undefined
							}>
								<Button
									icon={<MailOutlined />}
									data-testid="booking-link-send-email"
									loading={sendingChannel === "email"}
									disabled={busy || !notificationsEnabled || !patient?.email}
									onClick={() => handleSend("email")}
								>
									Send by email
								</Button>
							</Tooltip>
							<Tooltip title={
								!notificationsEnabled
									? "Notifications are switched off for this environment."
									: !patient?.phone ? "This patient has no phone number on file." : undefined
							}>
								<Button
									icon={<MessageOutlined />}
									data-testid="booking-link-send-sms"
									loading={sendingChannel === "sms"}
									disabled={busy || !notificationsEnabled || !patient?.phone}
									onClick={() => handleSend("sms")}
								>
									Send by SMS
								</Button>
							</Tooltip>
						</Space>

						{/*
							⚠️ The link id is not decoration. Every generate mints a NEW link, so a
							practitioner who clicked twice is looking at the second of two live links — and
							Revoke below acts on THIS one only.
						*/}
						<Text type="secondary" data-testid="booking-link-expiry" style={{ fontSize: 12 }}>
							{expiryLine}{linkId !== null ? ` Link #${linkId}.` : ""}
						</Text>

						<Divider style={{ margin: "4px 0" }} />

						<div>
							<Button
								type="text"
								danger
								size="small"
								icon={<StopOutlined />}
								data-testid="booking-link-revoke"
								disabled={busy}
								onClick={handleRevoke}
							>
								Revoke this link
							</Button>
							<Text type="secondary" style={{ fontSize: 11, display: "block" }}>
								Stops this link working — including any copy already sent to the patient.
							</Text>
						</div>
					</>
				) : (
					<>
						{issueFailed && (
							<Alert
								type="error"
								showIcon
								data-testid="booking-link-error"
								message="Could not prepare the link"
								description="The booking link could not be created. Try again."
							/>
						)}

						{isRevoked && (
							<Alert
								type="info"
								showIcon
								message="This link has been revoked"
								description="It no longer works, including any copy already sent to the patient."
							/>
						)}

						{/*
							Set only when a LIVE link was taken off screen by a scope change. It has not
							been revoked, and the practitioner may already have sent it — saying nothing
							would read as "that link is gone".
						*/}
						{supersededLinkId !== null && (
							<Alert
								type="info"
								showIcon
								message={`Link #${supersededLinkId} is still active. Changing the appointment type does not cancel it — generating again hands out a second link.`}
							/>
						)}

						<Button
							type="primary"
							data-testid="booking-link-generate"
							disabled={!patient?.id}
							onClick={() => issue()}
						>
							Generate booking link
						</Button>
					</>
				)}
			</div>
		</Modal>
	);
};

export default BookingLinkModal;
